import { useCallback, useEffect, useRef } from "react";
import { debugLog, debugWarn } from "../config/debug";

// A Screen Wake Lock API não está tipada na lib.dom do TypeScript 4.1, então declaramos
// só o mínimo que usamos.
type WakeLockSentinel = {
  released: boolean;
  release: () => Promise<void>;
  addEventListener: (type: "release", listener: () => void) => void;
};

type WakeLockNavigator = Navigator & {
  wakeLock?: { request: (type: "screen") => Promise<WakeLockSentinel> };
};

export function isScreenWakeLockSupported(): boolean {
  return typeof navigator !== "undefined" && "wakeLock" in navigator;
}

interface ScreenWakeLockCallbacks {
  onAcquired?: () => void;
  onReleased?: () => void;
  onError?: (reason: string) => void;
}

/**
 * Mantém a tela ligada enquanto `active` for verdadeiro — usado durante chamadas, a
 * partir do evento `presence` (busy/idle) do softphone. No Android a tela apagando por
 * inatividade suspende o WebView do Teams e corta o áudio da chamada.
 *
 * Duas características da API moldam o hook:
 * - o lock só pode ser adquirido com o documento visível;
 * - o browser o libera sozinho quando o documento é ocultado, e não o devolve na volta.
 *
 * Daí o listener de `visibilitychange`, que re-adquire o lock ao voltar para primeiro
 * plano se a chamada ainda estiver em curso. Essa liberação automática também é a razão
 * de não haver teto de tempo aqui: um `busy` que nunca vira `idle` fica limitado ao
 * período em que a tab está visível, e o botão power do aparelho sempre tem precedência
 * sobre o wake lock.
 */
export function useScreenWakeLock(active: boolean, callbacks: ScreenWakeLockCallbacks = {}): void {
  const sentinelRef = useRef<WakeLockSentinel | null>(null);
  // Mantidos em ref para não recriar o efeito (e portanto soltar/readquirir o lock) a
  // cada render do componente que passa os callbacks inline.
  const callbacksRef = useRef(callbacks);
  callbacksRef.current = callbacks;

  const release = useCallback(async () => {
    const sentinel = sentinelRef.current;
    if (!sentinel) return;
    sentinelRef.current = null;
    try {
      await sentinel.release();
      debugLog("[wakeLock] liberado");
      callbacksRef.current.onReleased?.();
    } catch (error) {
      debugWarn("[wakeLock] falha ao liberar:", error);
    }
  }, []);

  useEffect(() => {
    if (!isScreenWakeLockSupported()) return;

    let cancelled = false;

    const acquire = async () => {
      if (sentinelRef.current || document.visibilityState !== "visible") return;
      try {
        const sentinel = await (navigator as WakeLockNavigator).wakeLock!.request("screen");
        if (cancelled) {
          void sentinel.release();
          return;
        }
        sentinelRef.current = sentinel;
        // Dispara tanto na liberação automática (documento ocultado) quanto na nossa.
        sentinel.addEventListener("release", () => {
          if (sentinelRef.current === sentinel) sentinelRef.current = null;
        });
        debugLog("[wakeLock] adquirido");
        callbacksRef.current.onAcquired?.();
      } catch (error) {
        // Caso típico: a permission policy do host (o WebView do Teams) não delega
        // screen-wake-lock para a tab — a API existe mas o request é negado.
        const reason = error instanceof Error ? error.message : String(error);
        debugWarn("[wakeLock] falha ao adquirir:", error);
        callbacksRef.current.onError?.(reason);
      }
    };

    if (active) {
      void acquire();
    } else {
      void release();
    }

    const handleVisibilityChange = () => {
      if (active && document.visibilityState === "visible") void acquire();
    };

    document.addEventListener("visibilitychange", handleVisibilityChange);
    return () => {
      cancelled = true;
      document.removeEventListener("visibilitychange", handleVisibilityChange);
    };
  }, [active, release]);

  // Libera no unmount — o efeito acima só reage a mudanças de `active`.
  useEffect(() => () => void release(), [release]);
}
